import React, { FocusEvent, ChangeEvent } from 'react';
import { Form } from 'react-bootstrap';
import { InputFieldState } from '../types';

/**
 * PostalCodeFormコンポーネント用のPropsです。
 * @interface PostalCodeFormProps
 */
interface PostalCodeFormProps {
  /**
   * 郵便番号
   * @type {InputFieldState}
   * @memberof PostalCodeFormProps
   */
  postalCode: InputFieldState;

  /**
   * 郵便番号を設定する関数
   * @memberof PostalCodeFormProps
   */
  onChange: (postalCode: InputFieldState) => void;
}

/**
 * 郵便番号フォームのコンポーネントです。
 * @param {InputFieldState} props.postalCode - 表示する郵便番号
 * @param {function} props.onChange - 郵便番号が変更されたときに呼ばれるコールバック関数
 * @returns {JSX.Element} - 郵便番号フォームのJSX要素
 */
const PostalCodeForm: React.FC<PostalCodeFormProps> = ({ postalCode, onChange }) => {

  /**
   * フォームからフォーカスが外れたときのイベントハンドラーです。
   * 入力された郵便番号を整形し、onChange関数を呼び出します。
   * @param {object} event - フォーカスアウトのイベント
   * @returns {void}
   */
  const handleBlur = (event: FocusEvent<HTMLInputElement>): void => {
    const halfWidthText = event.target.value.replace(/[Ａ-Ｚａ-ｚ０-９]/g, function(s) {
      return String.fromCharCode(s.charCodeAt(0) - 0xfee0);
    });
    const deleteNonNumberInputValue = halfWidthText.replace(/[^0-9]/g, '').substring(0, 7);
    const formatIdentificationNumber: string = deleteNonNumberInputValue ? `${deleteNonNumberInputValue.slice(0, 3)}-${deleteNonNumberInputValue.slice(3, 7)}` : deleteNonNumberInputValue;
    const regexp = /\d{3}-\d{4}/;
    onChange({
      value: formatIdentificationNumber,
      errorMessage: formatIdentificationNumber === '' || regexp.test(formatIdentificationNumber) ? '' : '入力された郵便番号の形式が正しくありません。'
    });
  }

  /**
   * フォームの変更イベントハンドラーです。
   * 入力された郵便番号をonChange関数を呼び出します。
   * @param {object} event - フォームの変更イベント
   * @returns {void}
   */
  const handleChange = (event: ChangeEvent<HTMLInputElement>): void => {
    onChange({
      value: event.target.value,
      errorMessage: postalCode.errorMessage
    });
  }

  return (
    <Form.Group className="mb-2 row">
      <Form.Label className="col-lg-2 col-12 d-flex align-items-center">
        郵便番号
        <span className="text-small text-secondary ms-3">※任意</span>
      </Form.Label>
      <div className="col-lg-10 col-12">
        <Form.Control
          type="text"
          value={postalCode.value}
          placeholder="郵便番号を入力してください。"
          onBlur={handleBlur}
          onChange={handleChange}
          aria-label="郵便番号"
          isInvalid={postalCode.errorMessage !== ''}
        />
        {postalCode.errorMessage !== '' ? <div className="text-end text-danger small">{postalCode.errorMessage}</div> : ''}
      </div>
    </Form.Group>
  );
};

export default PostalCodeForm;